import * as React from 'react'
import { useState } from 'react'

import { observable } from 'mobx'
import { inject, observer } from 'mobx-react'

import EquipmentEditPanel from '~/client/src/desktop/components/EquipmentResource/EquipmentEditPanel'
import NavBar from '~/client/src/desktop/components/NavBar/NavBar'
import { Resource } from '~/client/src/desktop/components/Resource'
import ViewModeToggleGroup from '~/client/src/desktop/components/ViewModeToggleGroup/ViewModeToggleGroup'
import ViewModes from '~/client/src/desktop/enums/ViewModes'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import * as Layout from '~/client/src/shared/components/Layout'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import ProjectSetUp from '../ProjectSetUp/ProjectSetUp'
import EquipmentMapLayout from './EquipmentMapLayout'
import { EquipmentProvider } from './EquipmentProvider'

interface EquipmentProps {
  state?: DesktopInitialState
}

const EQUIPMENT_VIEW_MODES = [ViewModes.List, ViewModes.Map]

const Equipment: React.FC<EquipmentProps> = ({ state }) => {
  const projectId = state?.activeProject?.id
  const userId = state?.user?.id
  const [viewState] = useState(() =>
    observable({ viewMode: ViewModes.List as ViewModes }),
  )

  if (!projectId || !userId || !state) {
    return null
  }

  return (
    <Layout.View className="equipment">
      <Layout.Header className="layout-navigation">
        <NavBar />
      </Layout.Header>
      <Layout.Content className="tw:relative tw:overflow-hidden">
        <div className="tw:flex tw:items-center tw:h-full">
          <ProjectSetUp
            className="tw:flex tw:flex-col tw:h-full tw:max-w-fit"
            sidebarOnly
          />
          <div className="tw:flex tw:flex-1 tw:flex-col tw:h-full tw:min-w-0">
            <EquipmentProvider
              projectId={projectId}
              userId={userId}
              state={state}
            >
              <Layout.View>
                <Layout.Header>
                  <Resource.TopBar>
                    <Resource.SavedViews projectId={projectId} />
                  </Resource.TopBar>
                  <Resource.Header>
                    <Resource.Search />
                    <Resource.Separator />
                    <Resource.Filters />
                    <Resource.Actions>
                      <Resource.DisplayOptions />
                      <ViewModeToggleGroup
                        modes={EQUIPMENT_VIEW_MODES}
                        value={viewState.viewMode}
                        onChange={mode => (viewState.viewMode = mode)}
                      />
                    </Resource.Actions>
                  </Resource.Header>
                </Layout.Header>
                <Layout.Content>
                  {viewState.viewMode === ViewModes.Map ? (
                    <Resource.Content>
                      <EquipmentMapLayout />
                      <div className="tw:absolute tw:top-1.5 tw:right-4 tw:z-10">
                        <Resource.CreateButton />
                      </div>
                    </Resource.Content>
                  ) : (
                    <Resource.Content>
                      <Resource.ActionBar>
                        <div className="tw:flex tw:items-center tw:gap-2">
                          <Resource.CollapseToggle />
                          <Resource.RecordCount
                            label={c => Localization.translator.xEquipment(c)}
                          />
                          <Resource.DeleteButton
                            confirmationLabel={c =>
                              Localization.translator.confirmDeletionOfEquipment(
                                c,
                              )
                            }
                          />
                          <Resource.BulkEditButton />
                        </div>
                        <Resource.CreateButton />
                      </Resource.ActionBar>
                      <Resource.Table />
                    </Resource.Content>
                  )}
                </Layout.Content>
              </Layout.View>
              <EquipmentEditPanel />
              <Resource.BulkEditPanel />
            </EquipmentProvider>
          </div>
        </div>
      </Layout.Content>
      <Layout.Footer>
        <div />
      </Layout.Footer>
    </Layout.View>
  )
}

export default inject('state')(observer(Equipment))
